import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Loader } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useAppApi } from '@/lib/api';

const REDIRECT_MS = 1800;

/**
 * GitHub OAuth callback. GITHUB_REDIRECT_URI must point here.
 *
 * GitHub lands on this route with ?code=…&state=<userId>, plus installation_id when the
 * App flow was used. The code is exchanged SERVER-side (GithubConnection.connectCallback)
 * so the token is written straight to an encrypted column and never exists in the
 * browser. `state` is not trusted for identity — the server keys the row off the session.
 */
export default function GithubCallback() {
  const { fetchApi } = useAppApi();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ phase: 'working', login: null, error: null });
  // React 18 StrictMode mounts effects twice in dev. An OAuth code is single-use, so a
  // second exchange fails and would overwrite a successful result with an error.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const installationId = params.get('installation_id');
    const err = params.get('error');

    if (err) {
      const desc = params.get('error_description');
      setStatus({ phase: 'error', error: err === 'access_denied' ? 'You declined access.' : desc || err });
      return undefined;
    }
    if (!code) {
      setStatus({ phase: 'error', error: 'Missing authorization code.' });
      return undefined;
    }

    let timer;
    fetchApi('githubConnectCallback', { code, ...(installationId ? { installation_id: installationId } : {}) })
      .then((res) => {
        setStatus({ phase: 'done', login: res?.data?.login || null });
        timer = setTimeout(() => navigate('/github'), REDIRECT_MS);
      })
      .catch((ex) => {
        setStatus({
          phase: 'error',
          error: (ex.response && ex.response.data && ex.response.data.error) || ex.message || 'Failed to connect GitHub.',
        });
      });
    return () => clearTimeout(timer);
  }, [fetchApi, navigate]);

  return (
    <div className="flex justify-center py-10">
      <Card className="p-8 max-w-md w-full text-center">
        {status.phase === 'working' ? <Loader text="Connecting your GitHub account…" /> : null}

        {status.phase === 'done' ? (
          <>
            <CheckCircle2 size={40} className="mx-auto text-success-600" />
            <h1 className="mt-3 text-lg font-bold">GitHub connected</h1>
            <p className="mt-1 text-sm opacity-70">
              {status.login ? `@${status.login}` : 'Your account is linked.'} Redirecting…
            </p>
          </>
        ) : null}

        {status.phase === 'error' ? (
          <>
            <XCircle size={40} className="mx-auto text-error-600" />
            <h1 className="mt-3 text-lg font-bold">Couldn&apos;t connect</h1>
            <p className="mt-1 text-sm opacity-70">{status.error}</p>
            <Button className="mt-4" variant="secondary" onClick={() => navigate('/github')}>Back to GitHub</Button>
          </>
        ) : null}
      </Card>
    </div>
  );
}
